import { Component, Prop, State, h, Watch } from '@stencil/core';

@Component({
  tag: 'spw-search-field',
  styleUrl: 'spw-search-field.scss',
  shadow: true,
})
export class SpwSearchField {
  private hostElement: HTMLElement;

  /** Liste des éléments à filtrer, passée sous forme de chaîne JSON */
  @Prop() items?: string;
  /** Placeholder à afficher dans le champ de recherche */
  @Prop() placeholder: string = 'Recherche...';
  /** Label associé au champ de recherche */
  @Prop() label: string = '';
  /** Indique si le champ de recherche est désactivé */
  @Prop() disabled?: boolean;
  /** Nom du champ de recherche, utilisé pour les formulaires */
  @Prop() name: string;
  /** Indique si le champ est requis */
  @Prop() required?: boolean = false;
  /** Texte d'assistance affiché sous le champ de recherche */
  @Prop() assistiveText?: string;

  @State() filteredItems: Array<{ url?: string; label: string; category?: string }> = [];
  @State() searchText: string = '';
  @State() isOpen: boolean = false;
  @State() focusedItemIndex: number | null = null;
  @State() dropdownDirection: 'up' | 'down' = 'down';

  componentWillLoad() {
    if (this.items) {
      this.parseItems(this.items);
    } else {
      this.filteredItems = [];
    }
  }

  componentDidLoad() {
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  @Watch('items')
  parseItems(newValue: string) {
    if (!newValue) {
      this.filteredItems = [];
      return;
    }

    try {
      this.filteredItems = JSON.parse(newValue);
    } catch (e) {
      console.error('Invalid JSON format for items:', newValue);
      this.filteredItems = [];
    }
  }

  private handleClickOutside = (event: MouseEvent) => {
    if (this.isOpen) {
      const target = event.composedPath()[0] as Node;
      const isInsideHost = this.hostElement.contains(target);

      if (!isInsideHost) {
        this.isOpen = false;
      }
    }
  };

  private handleSearchInput(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchText = input.value;
    this.filterItems();
    this.determineDropdownDirection();
    this.isOpen = true;
  }

  private filterItems() {
    if (!this.items) {
      this.filteredItems = [];
      return;
    }

    const normalizedSearchText = this.normalizeString(this.searchText);

    this.filteredItems = JSON.parse(this.items).filter(item => this.normalizeString(item.label).includes(normalizedSearchText));
  }

  private normalizeString(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private selectItem(item: { url?: string; label: string }) {
    this.searchText = item.label;
    this.isOpen = false;

    if (item.url) {
      window.location.href = item.url;
    }
  }

  private determineDropdownDirection() {
    const rect = this.hostElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    if (rect.bottom + 200 > viewportHeight && rect.top > 200) {
      this.dropdownDirection = 'up';
    } else {
      this.dropdownDirection = 'down';
    }
  }

  private handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.isOpen) {
          this.focusedItemIndex = this.focusedItemIndex === null ? 0 : (this.focusedItemIndex + 1) % this.filteredItems.length;
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.isOpen) {
          this.focusedItemIndex =
            this.focusedItemIndex === null ? this.filteredItems.length - 1 : (this.focusedItemIndex - 1 + this.filteredItems.length) % this.filteredItems.length;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (this.isOpen && this.focusedItemIndex !== null) {
          this.selectItem(this.filteredItems[this.focusedItemIndex]);
        }
        break;
      case 'Escape':
        this.isOpen = false;
        break;
    }
  }

  private highlightMatch(item: string) {
    const normalizedSearchText = this.normalizeString(this.searchText);
    const normalizedItem = this.normalizeString(item);

    const startIndex = normalizedItem.indexOf(normalizedSearchText);

    if (startIndex === -1 || !this.searchText) {
      return item;
    }

    const endIndex = startIndex + normalizedSearchText.length;

    return (
      <span>
        {item.slice(0, startIndex)}
        <strong>{item.slice(startIndex, endIndex)}</strong>
        {item.slice(endIndex)}
      </span>
    );
  }

  render() {
    return (
      <div ref={el => (this.hostElement = el as HTMLElement)} class="spw-search-field">
        <spw-text-field
          placeholder={this.placeholder}
          name={this.name}
          label={this.label}
          is-search={true}
          is-clear={true}
          required={this.required}
          disabled={this.disabled}
          assistiveText={this.assistiveText}
          value={this.searchText}
          onInput={event => this.handleSearchInput(event)}
          onKeyDown={event => this.handleKeyDown(event)}
        ></spw-text-field>
        {this.isOpen && !!this.items && (
          <div class={`spw-search-field__options spw-search-field__options--${this.dropdownDirection}`}>
            {!!this.filteredItems.length && (
              <ul>
                {this.filteredItems.map((item, index) => (
                  <li
                    class={{
                      'spw-search-field__option': true,
                      'spw-search-field__option--focused': index === this.focusedItemIndex,
                    }}
                    onClick={() => this.selectItem(item)}
                  >
                    {this.highlightMatch(item.label)} {item.category && <span class="spw-tw-italic"> - {item.category}</span>}
                  </li>
                ))}
              </ul>
            )}
            {!this.filteredItems.length && <p class="spw-search-field__no-results">Pas de résultats.</p>}
          </div>
        )}
      </div>
    );
  }
}
